/**
 * Application Support Dashboard — BA team health view.
 *
 * Goal: answer "is the BA team in trouble?" at a glance.
 *
 * v1 (this file):
 *   - Big numbers: currently unresolved + resolved year-to-date
 *   - Status breakdown of unresolved tickets (where things are stuck)
 *   - Age distribution histogram (how stale is the queue?)
 *   - Recently resolved list (proof of throughput)
 *
 * v2 (deferred — needs more data):
 *   - Per-assignee workload (needs FS responder/agent field surfaced)
 *   - Trend over time (needs daily snapshot table)
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, BarChart3, Clock, ExternalLink, Loader2 } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import {
  useFreshservicePreview,
  useFreshserviceStatus,
  type FreshservicePreviewItem,
} from "@/hooks/useFreshservice";
import { cn } from "@/lib/cn";
import {
  BUCKET_META,
  BUCKET_ORDER,
  bucketFor,
  type HealthBucket,
} from "@/lib/healthBucket";

/** Compute days between an ISO date string and now. Negative = future. */
function daysAgo(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const t = Date.parse(isoDate);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

const AGE_BUCKETS: Array<{ label: string; min: number; max: number; tone: string }> = [
  { label: "<7 days", min: 0, max: 7, tone: "bg-emerald-500" },
  { label: "7–30", min: 7, max: 30, tone: "bg-amber-400" },
  { label: "30–90", min: 30, max: 90, tone: "bg-orange-500" },
  { label: "90+", min: 90, max: Infinity, tone: "bg-rose-600" },
];

export function ApplicationSupportDashboard() {
  const statusQ = useFreshserviceStatus();
  const isConfigured = statusQ.data?.configured === true;
  // BA queue: pull both Unresolved and Resolved (since-floor) for the
  // dashboard. Each cached independently by the existing hook.
  const unresolved = useFreshservicePreview(isConfigured, "open", "ba");
  const resolved = useFreshservicePreview(isConfigured, "closed", "ba");

  const tickets = unresolved.data ?? [];
  const resolvedTickets = resolved.data ?? [];

  // ---- Aggregations ----
  // Status breakdown: count unresolved tickets per status_label, sorted desc.
  const statusBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of tickets) {
      const label = t.status_label || "(unknown)";
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [tickets]);

  // Age distribution: bucket each unresolved ticket's age (days since
  // created_at) into the predefined ranges.
  const ageDistribution = useMemo(() => {
    const buckets = AGE_BUCKETS.map((b) => ({ ...b, count: 0 }));
    for (const t of tickets) {
      const age = daysAgo(t.created_at);
      if (age === null || age < 0) continue;
      for (const b of buckets) {
        if (age >= b.min && age < b.max) {
          b.count += 1;
          break;
        }
      }
    }
    return buckets;
  }, [tickets]);

  // KPI strip — six numbers that tell the story at a glance. The aging
  // counters are the ones that should make someone uncomfortable; we color
  // them when they cross thresholds so a glance is enough.
  const kpis = useMemo(() => {
    // MECE age partition — every unresolved ticket lands in exactly
    // one bucket, so age0_30 + age30_90 + age90plus === totalUnresolved.
    // This is what makes the headline math (Unresolved sums to its
    // age tiles) actually true on the page.
    let age0_30 = 0;
    let age30_90 = 0;
    let age90plus = 0;
    for (const t of tickets) {
      const age = daysAgo(t.created_at);
      if (age === null || age < 0) continue;
      if (age >= 90) age90plus += 1;
      else if (age >= 30) age30_90 += 1;
      else age0_30 += 1;
    }
    return { age0_30, age30_90, age90plus };
  }, [tickets]);

  // Per-BA workload: group unresolved tickets by agent_name and within
  // each group count by status_label. Tickets with no responder land in
  // "Unassigned". Sorted by total queue size desc — the busiest BA is
  // first. Each BA gets one donut.
  const perAgent = useMemo(() => {
    // Bucket key — use agent_id when known so groupings are stable
    // even if name overrides change. "unassigned" for null responder.
    type Bucket = {
      name: string;
      agentId: number | null;
      statuses: Map<string, number>;
      buckets: Record<HealthBucket, number>;
      aged30: number;
      aged90: number;
    };
    const byKey = new Map<string, Bucket>();
    for (const t of tickets) {
      const key = t.agent_id ? `id:${t.agent_id}` : "unassigned";
      const bucket = byKey.get(key) ?? {
        name: t.agent_name?.trim() || "Unassigned",
        agentId: t.agent_id ?? null,
        statuses: new Map<string, number>(),
        buckets: {
          open: 0,
          in_progress: 0,
          pending_approval: 0,
          waiting: 0,
          escalated: 0,
          on_hold: 0,
          other: 0,
        },
        aged30: 0,
        aged90: 0,
      };
      const status = t.status_label || "(unknown)";
      bucket.statuses.set(status, (bucket.statuses.get(status) ?? 0) + 1);
      bucket.buckets[bucketFor(status)] += 1;
      const age = daysAgo(t.created_at);
      if (age !== null && age >= 0) {
        if (age > 90) bucket.aged90 += 1;
        if (age > 30) bucket.aged30 += 1;
      }
      byKey.set(key, bucket);
    }
    const rows = Array.from(byKey.values()).map((b) => {
      const slices = Array.from(b.statuses.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
      const total = slices.reduce((sum, s) => sum + s.count, 0);
      return {
        name: b.name,
        agentId: b.agentId,
        total,
        slices,
        buckets: b.buckets,
        aged30: b.aged30,
        aged90: b.aged90,
      };
    });
    rows.sort((a, b) => b.total - a.total);
    return rows;
  }, [tickets]);

  const oldestUnresolved = useMemo(() => {
    return [...tickets]
      .filter((t) => t.created_at)
      .sort((a, b) => (a.created_at! < b.created_at! ? -1 : 1))
      .slice(0, 5);
  }, [tickets]);

  const recentlyResolved = useMemo(() => {
    // Resolved view returns tickets sorted FS-default; show the 10 most
    // recently created (rough proxy for most-recently-resolved given FS
    // doesn't expose closure date in the filter response).
    return [...resolvedTickets]
      .filter((t) => t.created_at)
      .sort((a, b) => (a.created_at! < b.created_at! ? 1 : -1))
      .slice(0, 10);
  }, [resolvedTickets]);

  // ---- Loading / unconfigured ----
  if (statusQ.isLoading) {
    return (
      <>
        <TopBar title="Application Support · Dashboard" subtitle="BA team health" />
        <div className="p-8 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      </>
    );
  }
  if (!isConfigured) {
    return (
      <>
        <TopBar title="Application Support · Dashboard" subtitle="BA team health" />
        <div className="p-8">
          <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-6 text-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <h2 className="font-semibold text-amber-900 dark:text-amber-300">Freshservice isn't connected</h2>
                <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                  Configure it in Settings → Integrations to populate this dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const isLoading = unresolved.isLoading || resolved.isLoading;
  const totalUnresolved = tickets.length;
  const totalResolved = resolvedTickets.length;
  const maxAgeBucket = Math.max(...ageDistribution.map((b) => b.count), 1);
  const maxStatus = Math.max(...statusBreakdown.map((s) => s.count), 1);

  return (
    <>
      <TopBar
        title="Application Support · Dashboard"
        subtitle="BA team queue health · Year-to-date"
      >
        <Link
          to="/application-support"
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised transition-colors"
        >
          See all tickets <ArrowRight className="h-3 w-3" />
        </Link>
      </TopBar>

      <div className="flex-1 overflow-y-auto bg-canvas">
        <div className="mx-auto max-w-5xl space-y-6 px-6 py-6">
          {isLoading && (
            <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading tickets…
            </div>
          )}

          {!isLoading && (
            <>
              {/* ----- KPI strip — totals first, then MECE age partition.
                   age0_30 + age30_90 + age90plus === totalUnresolved
                   so the math sums on the page. ----- */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <KpiCard
                  label="Unresolved"
                  value={totalUnresolved}
                  tone="indigo"
                  href="/application-support?view=open"
                />
                <KpiCard
                  label="Resolved YTD"
                  value={totalResolved}
                  tone="emerald"
                  href="/application-support?view=closed"
                />
                <KpiCard
                  label="Last 30 days"
                  value={kpis.age0_30}
                  tone="sky"
                  href="/application-support?view=open&age=lt30"
                />
                <KpiCard
                  label="30–90 days"
                  value={kpis.age30_90}
                  tone={kpis.age30_90 > 0 ? "amber" : "neutral"}
                  href="/application-support?view=open&age=30to90"
                />
                <KpiCard
                  label="90+ days"
                  value={kpis.age90plus}
                  tone={kpis.age90plus > 0 ? "rose" : "neutral"}
                  href="/application-support?view=open&age=gt90"
                />
              </div>

              {/* ----- Per-BA workload (donuts) ----- */}
              {perAgent.length > 0 && (
                <Card
                  title="Workload by BA"
                  subtitle={`Unresolved tickets per agent · grouped into health states · click a card to filter · ${perAgent.length} ${perAgent.length === 1 ? "agent" : "agents"}`}
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {perAgent.map((agent) => (
                      <AgentDonut key={agent.name} agent={agent} />
                    ))}
                  </div>
                </Card>
              )}

              {/* ----- Status breakdown + Age distribution ----- */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card title="Unresolved by status" subtitle="Where the queue is sitting">
                  {statusBreakdown.length === 0 ? (
                    <div className="text-xs italic text-ink-subtle">No unresolved tickets.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {statusBreakdown.map((s) => (
                        <StatusBar key={s.label} label={s.label} count={s.count} max={maxStatus} />
                      ))}
                    </div>
                  )}
                </Card>

                <Card title="Age of unresolved tickets" subtitle="Days since creation">
                  <div className="space-y-2">
                    {ageDistribution.map((b) => (
                      <div key={b.label} className="flex items-center gap-3">
                        <span className="w-16 shrink-0 text-[11px] font-medium text-ink-3">
                          {b.label}
                        </span>
                        <div className="h-4 flex-1 overflow-hidden rounded bg-raised">
                          <div
                            className={cn("h-full rounded", b.tone)}
                            style={{ width: `${(b.count / maxAgeBucket) * 100}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-[11px] font-bold tabular-nums text-ink-2">
                          {b.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* ----- Oldest unresolved + Recently resolved ----- */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card
                  title="Oldest unresolved"
                  subtitle="Tickets that have sat the longest"
                  icon={<Clock className="h-3.5 w-3.5 text-amber-500" />}
                >
                  {oldestUnresolved.length === 0 ? (
                    <div className="text-xs italic text-ink-subtle">No unresolved tickets.</div>
                  ) : (
                    <ul className="space-y-2">
                      {oldestUnresolved.map((t) => (
                        <TicketLine key={t.ticket_id} ticket={t} showAge />
                      ))}
                    </ul>
                  )}
                </Card>

                <Card
                  title="Recently resolved"
                  subtitle={`Last ${recentlyResolved.length} closed (created in the current period)`}
                >
                  {recentlyResolved.length === 0 ? (
                    <div className="text-xs italic text-ink-subtle">
                      No recently resolved tickets.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {recentlyResolved.map((t) => (
                        <TicketLine key={t.ticket_id} ticket={t} />
                      ))}
                    </ul>
                  )}
                </Card>
              </div>

              {/* ----- v2 hint ----- */}
              <div className="rounded-lg border border-line bg-surface p-3 text-[11px] text-ink-muted">
                <BarChart3 className="inline h-3 w-3 mr-1 text-ink-subtle" />
                <strong className="text-ink-2">Coming next:</strong> week-over-week trend
                (waiting on daily snapshots).
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Single KPI card for the top strip. Tone drives color semantics:
 *   indigo / emerald / sky — informational
 *   amber  — warn (aging)
 *   rose   — alert (badly aging)
 *   neutral — zero / inactive (don't shout when there's nothing to see)
 */
function KpiCard({
  label,
  value,
  tone,
  href,
}: {
  label: string;
  value: number;
  tone: "indigo" | "emerald" | "amber" | "rose" | "sky" | "neutral";
  href?: string;
}) {
  const palette: Record<typeof tone, string> = {
    indigo: "border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50/60 dark:bg-brand-navy-500/10 text-brand-navy-700 dark:text-brand-navy-300",
    emerald: "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    amber: "border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300",
    rose: "border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 text-rose-800 dark:text-rose-300",
    sky: "border-sky-200 dark:border-sky-500/30 bg-sky-50/60 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300",
    neutral: "border-line bg-surface text-ink-muted",
  };
  const className = cn(
    "block rounded-xl border p-3 shadow-sm transition-all",
    palette[tone],
    href && "hover:-translate-y-0.5 hover:shadow-md",
  );
  const inner = (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums leading-none">
        {value.toLocaleString()}
      </div>
    </>
  );
  return href ? (
    <Link to={href} className={className} title={`See tickets matching "${label}"`}>
      {inner}
    </Link>
  ) : (
    <div className={className}>{inner}</div>
  );
}

function Card({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4 shadow-sm">
      <div className="mb-3">
        <div className="flex items-center gap-1.5">
          {icon}
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
        </div>
        {subtitle && <p className="mt-0.5 text-[11px] text-ink-muted">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function StatusBar({ label, count, max }: { label: string; count: number; max: number }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 shrink-0 truncate text-[11px] font-medium text-ink-3" title={label}>
        {label}
      </span>
      <div className="h-3 flex-1 overflow-hidden rounded bg-raised">
        <div
          className="h-full rounded bg-brand-navy-500"
          style={{ width: `${(count / max) * 100}%` }}
        />
      </div>
      <span className="w-8 text-right text-[11px] font-bold tabular-nums text-ink-2">
        {count}
      </span>
    </div>
  );
}

function TicketLine({ ticket, showAge }: { ticket: FreshservicePreviewItem; showAge?: boolean }) {
  const age = showAge ? daysAgo(ticket.created_at) : null;
  return (
    <li className="flex items-start gap-2 text-[12px]">
      <span className="font-mono text-[11px] text-ink-subtle shrink-0">
        #{ticket.ticket_id}
      </span>
      <a
        href={ticket.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 truncate text-ink-2 hover:text-brand-navy-600 dark:hover:text-brand-navy-400 hover:underline"
        title={ticket.subject}
      >
        {ticket.subject}
      </a>
      {age !== null && (
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
            age > 90
              ? "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300"
              : age > 30
                ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300"
                : "bg-raised text-ink-3",
          )}
        >
          {age}d
        </span>
      )}
      <a
        href={ticket.url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 text-ink-faint hover:text-brand-navy-500"
        title="Open in Freshservice"
      >
        <ExternalLink className="h-3 w-3" />
      </a>
    </li>
  );
}

// ---------------------------------------------------------------------------
// BA card — answers "is this BA stuck?" not "what's their status mix?"
//   Header: name + queue size (rich KPI feel)
//   Body:   horizontal stacked bar grouped into Active / Waiting / Blocked /
//           Other. Twelve raw statuses are reduced to four health states
//           that an exec can read at a glance. Raw status counts surface
//           on hover via the bar's title tooltip.
//   Footer: aging callout — number over 30d / over 90d, with a rose
//           accent when 90+ exists. This is the "stuck" signal.
// Click anywhere → filtered ticket list for that BA.
// ---------------------------------------------------------------------------

interface AgentRow {
  name: string;
  agentId: number | null;
  total: number;
  slices: Array<{ label: string; count: number }>;
  buckets: Record<HealthBucket, number>;
  aged30: number;
  aged90: number;
}

function AgentDonut({ agent }: { agent: AgentRow }) {
  const agentParam = agent.agentId ?? "unassigned";
  const href = `/application-support?agent=${agentParam}`;

  // Donut geometry — stroke arcs around a circle, one per bucket with
  // a non-zero count. Order is the canonical BUCKET_ORDER so colors
  // appear in the same sequence on every card (Open at the top,
  // Escalated at the bottom-right, etc.).
  const size = 130;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  let offset = 0;
  const arcs = BUCKET_ORDER.filter((b) => agent.buckets[b] > 0).map((b) => {
    const frac = agent.buckets[b] / agent.total;
    const dash = frac * c;
    const arc = (
      <circle
        key={b}
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={BUCKET_META[b].hex}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${c - dash}`}
        strokeDashoffset={-offset}
        // 12 o'clock start instead of 3 o'clock
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    );
    offset += dash;
    return arc;
  });

  // Per-bucket raw status breakdown (e.g. Escalated → "Escalated: 9,
  // Jira: 2"). Used as the legend chip's title so hovering tells you
  // which FS statuses are actually in that bucket for this BA.
  const breakdownByBucket = useMemo(() => {
    const out: Record<HealthBucket, string[]> = {
      open: [],
      in_progress: [],
      pending_approval: [],
      waiting: [],
      escalated: [],
      on_hold: [],
      other: [],
    };
    for (const s of agent.slices) {
      out[bucketFor(s.label)].push(`${s.label}: ${s.count}`);
    }
    return out;
  }, [agent.slices]);

  const escalated = agent.buckets.escalated;
  const onHold = agent.buckets.on_hold;
  const stuck = escalated + onHold;
  const hasCrit = agent.aged90 > 0;
  const hasWarn = !hasCrit && agent.aged30 > 0;
  const visibleBuckets = BUCKET_ORDER.filter((b) => agent.buckets[b] > 0);

  return (
    <Link
      to={href}
      className="group block rounded-xl border border-line bg-surface p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-navy-300 hover:shadow-md"
      title={`See ${agent.name}'s tickets`}
    >
      {/* Header — just name; total lives in the donut center */}
      <div className="mb-3 truncate text-[13px] font-semibold text-ink" title={agent.name}>
        {agent.name}
      </div>

      {/* Donut + legend */}
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="#f1f5f9"
              strokeWidth={stroke}
            />
            {arcs}
          </svg>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[28px] font-bold leading-none tabular-nums text-ink">
              {agent.total}
            </span>
            <span className="mt-1 text-[9px] uppercase tracking-wider text-ink-muted">
              {agent.total === 1 ? "ticket" : "tickets"}
            </span>
          </div>
        </div>
        {/* Legend — each chip links to that bucket's filtered list */}
        <ul className="min-w-0 flex-1 space-y-1 text-[11px]">
          {visibleBuckets.map((b) => (
            <li key={b}>
              <Link
                to={`/application-support?agent=${agentParam}&bucket=${b}`}
                onClick={(e) => e.stopPropagation()}
                title={breakdownByBucket[b].join("\n") || BUCKET_META[b].label}
                className="flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:bg-raised"
              >
                <span
                  className={cn("inline-block h-2 w-2 shrink-0 rounded-full", BUCKET_META[b].dot)}
                />
                <span className={cn("min-w-0 flex-1 truncate font-medium", BUCKET_META[b].text)}>
                  {BUCKET_META[b].label}
                </span>
                <span className="shrink-0 font-bold tabular-nums text-ink-2">
                  {agent.buckets[b]}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Aging callout — only shown when something's worth flagging */}
      {(agent.aged30 > 0 || stuck > 0) && (
        <div
          className={cn(
            "mt-3 flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[11px]",
            hasCrit
              ? "bg-rose-50 dark:bg-rose-500/10 text-rose-800 dark:text-rose-300"
              : hasWarn
                ? "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300"
                : "bg-canvas text-ink-3",
          )}
        >
          <span className="flex items-center gap-1">
            <Clock
              className={cn(
                "h-3 w-3 shrink-0",
                hasCrit ? "text-rose-500" : hasWarn ? "text-amber-500" : "text-ink-subtle",
              )}
            />
            <span className="tabular-nums">
              {agent.aged30 > 0 && (
                <>
                  <strong>{agent.aged30}</strong> over 30d
                </>
              )}
              {agent.aged30 > 0 && agent.aged90 > 0 && " · "}
              {agent.aged90 > 0 && (
                <>
                  <strong>{agent.aged90}</strong> over 90d
                </>
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {escalated > 0 && (
              <Link
                to={`/application-support?agent=${agentParam}&bucket=escalated`}
                onClick={(e) => e.stopPropagation()}
                title={breakdownByBucket.escalated.join("\n")}
                className="rounded-full bg-rose-100 dark:bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-700 dark:text-rose-300 transition-colors hover:bg-rose-200"
              >
                {escalated} esc
              </Link>
            )}
            {onHold > 0 && (
              <Link
                to={`/application-support?agent=${agentParam}&bucket=on_hold`}
                onClick={(e) => e.stopPropagation()}
                title={breakdownByBucket.on_hold.join("\n")}
                className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold text-ink-2 transition-colors hover:bg-muted-strong"
              >
                {onHold} hold
              </Link>
            )}
          </span>
        </div>
      )}
    </Link>
  );
}
